/**
 * Scope the mobile-navigation focus trap to the level on screen.
 *
 * The drawer is a stack, not one panel: every level is rendered up front inside
 * `#mobile-navigation` and slid into place with a transform (`.menu-drawer`,
 * revealed by `.menu-opening`). The levels *behind* the open one stay laid out
 * and `visibility: visible`, merely covered — so the drawer-level trap contains
 * focus correctly but Tab still walks through the parent levels' items, the
 * search field and the root Close button, all painted over.
 *
 * This marks everything outside the deepest open level inert, bounded by the
 * drawer root. The trap needs no change: it re-queries its focusable set per
 * Tab and rejects anything inside an `[inert]` subtree.
 *
 * Two rules, both keyed off state the theme already maintains:
 *
 *   1. A closed sliding level (`.menu-drawer.invisible` without
 *      `.menu-opening`) is inert. Redundant with `visibility: hidden` at rest,
 *      but not during the 500ms slide-out, where the closing panel keeps its
 *      computed visibility while its links are already off screen.
 *   2. Everything between the deepest open level and the drawer root is inert
 *      while that level is open.
 *
 * Accordion and carousel menu blocks are untouched: they expand in place with
 * `x-collapse`, never receive `.menu-opening`, and do not carry `invisible`.
 *
 * Depth is derived from the DOM rather than declared as `:inert` bindings in
 * Liquid, where the six blocks in `sections/mobile-navigation.liquid` and three
 * nesting depths in `snippets/mobile-main-menu.liquid` would each need their own
 * depth comparison, ~15 bindings that must agree.
 */
(() => {
  'use strict';

  const ROOT_ID = 'mobile-navigation';

  /** A level in the sliding stack — as opposed to an accordion panel. */
  const LEVEL_SELECTOR = '.menu-drawer.invisible';
  const OPEN_CLASS = 'menu-opening';

  /** Attributes we added on the last sync, so they can be reverted exactly. */
  let marked = null;

  /** The deepest open level at the last sync, to detect in/out transitions. */
  let lastOpen = null;

  const api = () => window.FarmacyA11y || {};

  /**
   * The open level nearest the top of the stack.
   *
   * Depth is counted in DOM ancestors, not read from
   * `$store.xMobileNav.currentMenuLinks`: that array is a flat list of opaque
   * path tokens with no ordering guarantee (`removeActiveLink` splices by
   * value) and the accordion branch pushes to it without ever removing.
   */
  const deepestOpenLevel = (root) => {
    let deepest = null;
    let deepestDepth = -1;

    for (const level of root.querySelectorAll(`${LEVEL_SELECTOR}.${OPEN_CLASS}`)) {
      let depth = 0;
      for (let node = level.parentElement; node && node !== root; node = node.parentElement) depth++;
      if (depth > deepestDepth) {
        deepestDepth = depth;
        deepest = level;
      }
    }

    return deepest;
  };

  /**
   * The control that opened `level`, for restoring focus on Back. Every level is
   * emitted immediately after the row holding its disclosure button — at all
   * three depths, in both the section and the snippet — so that row is the
   * previous element sibling.
   */
  const openerFor = (level) => {
    const row = level.previousElementSibling;
    if (!row) return null;
    return row.matches('button') ? row : row.querySelector('button');
  };

  /**
   * Move focus into a newly opened level.
   *
   * Deferred through `focusWhenReady` rather than focused directly: the level is
   * revealed by a class change that starts a 500ms transform-and-visibility
   * transition, so on the frame the class lands its children can still fail a
   * visibility test. It moves focus without engaging a second trap — the
   * drawer's own trap is already active and now covers exactly this level.
   */
  const focusInto = (level) => {
    const store = window.Alpine && window.Alpine.store && window.Alpine.store('xFocusElement');
    if (store && typeof store.focusWhenReady === 'function') {
      store.focusWhenReady(level);
      return;
    }
    requestAnimationFrame(() => {
      const first = level.querySelector('button, a[href], input, select, textarea');
      if (first) first.focus();
    });
  };

  const focusOpener = (level) => {
    const opener = openerFor(level);
    if (!opener) return;
    requestAnimationFrame(() => {
      const focusable = api().isFocusable;
      if (typeof focusable === 'function' && !focusable(opener)) return;
      opener.focus();
    });
  };

  const sync = () => {
    const root = document.getElementById(ROOT_ID);
    if (!root) return;

    const { markInertOutside, unmarkInert } = api();
    if (typeof markInertOutside !== 'function') return;

    if (marked) {
      unmarkInert(marked);
      marked = null;
    }

    // Rule 1. Applied whether or not the drawer is open, so a level sliding out
    // is sealed for the whole of its transition.
    const closed = [];
    for (const level of root.querySelectorAll(LEVEL_SELECTOR)) {
      if (level.classList.contains(OPEN_CLASS)) continue;
      if (level.hasAttribute('inert')) continue;
      level.setAttribute('inert', '');
      closed.push({ el: level, addedInert: true, addedAriaHidden: false });
    }

    const drawerOpen = root.classList.contains(OPEN_CLASS);
    const open = deepestOpenLevel(root);

    // Rule 2. A shut drawer is handled by the `:inert` binding on the root in
    // sections/mobile-navigation.liquid; nothing to scope inside it.
    const behind = open && drawerOpen ? markInertOutside(open, root) : [];

    marked = closed.concat(behind);

    if (open === lastOpen) return;

    const previous = lastOpen;
    lastOpen = open;

    // Focus only follows the level change while the drawer is on screen:
    // `currentMenuLinks` is not reset on close, so level state can change with
    // the drawer shut (a theme-editor section reload, say).
    if (!drawerOpen) return;

    if (open && (!previous || !open.contains(previous))) {
      // Descended into a level the user just opened.
      focusInto(open);
    } else if (previous) {
      // Went Back: `open` is an ancestor of the level we left, or the stack is
      // empty and we are on the root menu.
      focusOpener(previous);
    }
  };

  /**
   * Coalesce a burst of mutations into one sync per frame — Alpine touches
   * `class` all over this subtree far more often than level state changes.
   */
  let scheduled = false;
  const scheduleSync = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      sync();
    });
  };

  const observeRoot = (root) => {
    sync();

    // ⚠ `class` only. The sweep writes `inert` and `aria-hidden`, so a wider
    // filter would retrigger this observer on its own work, forever.
    new MutationObserver(scheduleSync).observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  };

  /**
   * `#mobile-navigation` does not exist at load: `layout/theme.liquid` wraps it
   * in `<template x-if="!runDispatch">` and `runDispatch` only flips on the
   * first mouseover / touchstart. Wait for the node, then stop watching.
   */
  const start = () => {
    const existing = document.getElementById(ROOT_ID);
    if (existing) {
      observeRoot(existing);
      return;
    }

    const bootstrap = new MutationObserver(() => {
      const root = document.getElementById(ROOT_ID);
      if (!root) return;
      bootstrap.disconnect();
      observeRoot(root);
    });

    bootstrap.observe(document.body, { subtree: true, childList: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
